import { Store } from "jotai/vanilla/store"
import jsonStableStringify from "json-stable-stringify"
import { orderBy } from "lodash"
import capitalize from "lodash/capitalize"
import hasIn from "lodash/hasIn"
import snakeCase from "lodash/snakeCase"

import { studentAtoms } from "#atoms"
import { Student } from "#classes/Student"
import { type MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type TeacherConstructor } from "#customTypes/composedTypes/teacherTypes"
import { type TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { type DataForDisplay } from "#customTypes/globalTypes"

export const stringify = (data: unknown): string =>
  JSON.stringify(data, null, 2)

export const stableStringify = (data: unknown): string =>
  jsonStableStringify(data) ?? "{}"

export const camelToWords = (str?: string): string => {
  return capitalize(snakeCase(str).split("_").join(" "))
}

// Coerce a value (often a string number like "3" or "12") to a number for
// numeric table sorting. Non-numeric values fall back to 0 so they sort first.
// Used to grab a field from an array of ids, like names or emails
export const getFormattedField = <T>(data: T[], field: keyof T): string => {
  return data.map((item) => item[field]).join(", ")
}

export type ErrorType = Error | string | { message: string }

export const formatError = (error: ErrorType): string => {
  if (!error) return ""

  if (error instanceof Error || hasIn(error, "message")) {
    return (error as Error).message
  } else if (typeof error === "string") {
    return error
  } else {
    return "Unknown error 🤔"
  }
}

export const formatErrorArray = (errors: ErrorType[]): string => {
  if (!errors || errors.length === 0) return ""

  return errors.map(formatError).join("\n")
}

export const formatStudents = (
  students: StudentConstructor[]
): DataForDisplay[] => {
  if (!students) return []

  return orderBy(students, "name", "asc").map((student) => {
    const studentObj = new Student(student)
    return {
      id: studentObj.id,
      label: studentObj.getNameAndDob(),
    }
  })
}

export const formatTeachers = (
  teachers: TeacherConstructor[]
): DataForDisplay[] => {
  if (!teachers) return []

  return orderBy(teachers, "name", "asc").map((teacher) => ({
    id: teacher.id,
    label: teacher.name,
  }))
}

export const formatMakeups = (
  makeups: MakeupConstructor[],
  store: Store
): DataForDisplay[] => {
  if (!makeups) return []

  return makeups
    .map((makeup) => {
      const studentData = store.get(studentAtoms.docByIdAtom(makeup.studentId))

      if (!studentData) return { id: makeup.id, label: "⚠️ Student not found" }

      const student = new Student(studentData)
      return {
        id: makeup.id,
        label: student ? student.getNameAndDob() : "⚠️ Student not found",
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const formatTryouts = (
  tryouts: TryoutConstructor[],
  store: Store
): DataForDisplay[] => {
  if (!tryouts) return []

  return tryouts
    .map((tryout) => {
      const studentData = store.get(studentAtoms.docByIdAtom(tryout.studentId))

      if (!studentData) return { id: tryout.id, label: "⚠️ Student not found" }

      const student = new Student(studentData)
      return {
        id: tryout.id,
        label: student ? student.getNameAndDob() : "⚠️ Student not found",
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}
